import React, { useState, useEffect } from 'react';
import { useSearchBox } from 'react-instantsearch';
import SearchIcon from '@mui/icons-material/Search';
import SettingsIcon from '@mui/icons-material/Settings';
import Box from '../Atoms/Box';
import Typography from '../Atoms/Typography';
import Select from '../Atoms/Select';
import Button from '../Atoms/Button/Button';
import TextField from '../Atoms/TextField/TextField';

interface PageSizeOption {
  value: number;
  label: string;
}

interface AlgoliaTableHeaderProps {
  tableTitle: string;
  pageSize: number;
  pageSizeOptions: PageSizeOption[];
  onPageSizeChange: (value: string | number | object) => void;
  onConfigureColumns: () => void;
}

const AlgoliaTableHeader: React.FC<AlgoliaTableHeaderProps> = ({
  tableTitle,
  pageSize,
  pageSizeOptions,
  onPageSizeChange,
  onConfigureColumns,
}) => {
  const { query, refine } = useSearchBox();
  const [searchValue, setSearchValue] = useState(query);

  // Sync search value with Algolia query
  useEffect(() => {
    if (query !== searchValue) {
      setSearchValue(query);
    }
  }, [query, searchValue]);

  // Handle search input changes
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setSearchValue(newValue);
    refine(newValue);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
        p: 2,
        bgcolor: 'background.paper',
        width: '100%',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', width: '40%', gap: 2 }}>
        <TextField
          label=""
          placeholder={`Search ${tableTitle}...`}
          value={searchValue}
          onChange={handleSearchChange}
          variant="outlined"
          fullWidth
          sx={{
            '& .MuiOutlinedInput-root': {
              height: '40px',
            },
          }}
          InputProps={{
            startAdornment: (
              <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                <SearchIcon color="action" />
              </Box>
            ),
          }}
        />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2">Rows per page:</Typography>
          <Select
            id="page-size-select"
            label=""
            value={pageSize}
            list={pageSizeOptions}
            onChangeHandler={onPageSizeChange}
            size="small"
            sx={{
              mt: 0,
              minWidth: 80,
              '& .MuiOutlinedInput-root': {
                height: '32px',
              },
            }}
            fullWidth={false}
          />
        </Box>
        <Button
          variant="outlined"
          size="small"
          onClick={onConfigureColumns}
          sx={{
            height: '32px',
            minWidth: 'auto',
            padding: '6px',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
          aria-label="Configure Columns"
        >
          <SettingsIcon />
        </Button>
      </Box>
    </Box>
  );
};

export default AlgoliaTableHeader;
